import { apiClient } from '@/api/client';
import { z } from 'zod';
import { User } from '@/types/user';
import { mapUserWireToDomain } from '../mappers/user';
import { userWireSchema, type PaginatedUserResponse as PaginatedUserResponseWire, type UserWire } from '../schemas/user';

// Re-export PaginatedUserResponse for external use
export type PaginatedUserResponse = PaginatedUserResponseWire;

/**
 * Domain-oriented API module for user operations
 */

export interface GetUserResponse {
  user: User;
}

/**
 * Get current user's profile
 * Validates and maps wire format to domain format
 * Note: Backend requires user ID in path even for current user
 */
export async function getCurrentUser(userId: string): Promise<GetUserResponse> {
  // Use /api/user/:id endpoint (backend requires user ID in path)
  const wireUser = await apiClient.get<UserWire>(`/api/user/${userId}`);

  // Validate response against wire schema with error handling
  let validatedWire: UserWire;
  try {
    validatedWire = userWireSchema.parse(wireUser);
  } catch (error) {
    // Log validation errors but don't expose them to UI
    if (error instanceof z.ZodError) {
      console.error('[API] User data validation failed:', error.issues);
      // For now, use safeParse and log issues, but continue with raw data
      // In production, you might want to throw a more user-friendly error
      const parseResult = userWireSchema.safeParse(wireUser);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        // If validation completely fails, throw a user-friendly error
        throw new Error('Invalid user data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  const user = mapUserWireToDomain(validatedWire);

  return { user };
}

/**
 * Get user profile by ID
 * Validates and maps wire format to domain format
 */
export async function getUserById(userId: string): Promise<GetUserResponse> {
  const wireUser = await apiClient.get<UserWire>(`/api/user/${userId}`);

  // Validate response against wire schema with error handling
  let validatedWire: UserWire;
  try {
    validatedWire = userWireSchema.parse(wireUser);
  } catch (error) {
    // Log validation errors but don't expose them to UI
    if (error instanceof z.ZodError) {
      console.error('[API] User data validation failed:', error.issues);
      const parseResult = userWireSchema.safeParse(wireUser);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid user data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  const user = mapUserWireToDomain(validatedWire);

  return { user };
}

/**
 * Get user profile by username
 * Validates and maps wire format to domain format
 * Username can include or exclude the @ prefix
 */
export async function getUserByUsername(username: string): Promise<GetUserResponse> {
  // Remove @ prefix if present and normalize
  const normalizedUsername = username.replace(/^@+/, '');
  const wireUser = await apiClient.get<UserWire>(`/api/user/username/${normalizedUsername}`);

  // Validate response against wire schema with error handling
  let validatedWire: UserWire;
  try {
    validatedWire = userWireSchema.parse(wireUser);
  } catch (error) {
    // Log validation errors but don't expose them to UI
    if (error instanceof z.ZodError) {
      console.error('[API] User data validation failed:', error.issues);
      const parseResult = userWireSchema.safeParse(wireUser);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid user data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  const user = mapUserWireToDomain(validatedWire);

  return { user };
}

/**
 * Update current user's profile
 */
export interface UpdateUserRequest {
  name?: string;
  username?: string;
  bio?: string;
  image?: string;
}

export async function updateUserProfile(data: UpdateUserRequest): Promise<User> {
  const wireUser = await apiClient.patch<UserWire>('/api/user/profile', data);

  // Validate and map response with error handling
  let validatedWire: UserWire;
  try {
    validatedWire = userWireSchema.parse(wireUser);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] User update validation failed:', error.issues);
      const parseResult = userWireSchema.safeParse(wireUser);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid user data received from server');
      }
    } else {
      throw error;
    }
  }
  return mapUserWireToDomain(validatedWire);
}

/**
 * Upload profile photo
 */
export interface UploadPhotoResponse {
  message: string;
  file_path: string;
  file_url: string;
}

export async function uploadProfilePhoto(
  photoFile: { uri: string; type?: string; name?: string }
): Promise<UploadPhotoResponse> {
  return apiClient.uploadFile<UploadPhotoResponse>(
    '/api/user/upload-photo',
    photoFile,
    'photo'
  );
}

/**
 * Query parameters for searching users
 */
export interface SearchUsersParams {
  query?: string;
  page?: number;
  per_page?: number;
}

/**
 * Search users with text query
 *
 * @param params - Search parameters including query text and pagination
 * @returns Paginated list of users matching the search
 *
 * @example
 * ```typescript
 * // Search for users matching "john"
 * const result = await searchUsers({
 *   query: 'john',
 *   per_page: 20
 * });
 * ```
 */
export async function searchUsers(
  params: SearchUsersParams = {}
): Promise<PaginatedUserResponse> {
  const queryParams: string[] = [];

  if (params.query) {
    queryParams.push(`query=${encodeURIComponent(params.query)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/user/search${queryString}`;

  return apiClient.get<PaginatedUserResponse>(endpoint);
}
